import React, { useEffect } from "react";
import { todayISO } from "../config/dateUtils";

const FREQ_OPTIONS = ["Daily", "Weekly", "Monthly", "Less than monthly"];

const SPRAY_SITE_OPTIONS = [
  { key: "scalp", label: "Scalp Hair" },
  { key: "neck", label: "Neck" },
  { key: "arms", label: "Arms" },
  { key: "chest", label: "Chest" },
  { key: "underarms", label: "Underarms" },
  { key: "legs", label: "Legs" },
  { key: "all_over", label: "All Over" },
];

const spraySiteField = (siteKey) => `sprays_sites_${siteKey}`;

const BODY_HAIR_SITE_GROUPS = [
  {
    key: "arms",
    label: "Arms",
    subsites: [
      { key: "left_arm", label: "Left arm" },
      { key: "right_arm", label: "Right arm" },
    ],
  },
  {
    key: "legs",
    label: "Legs",
    subsites: [
      { key: "left_leg", label: "Left leg" },
      { key: "right_leg", label: "Right leg" },
    ],
  },
  {
    key: "chest",
    label: "Chest",
    subsites: [
      { key: "chest_e", label: "Chest E" },
      { key: "chest_t", label: "Chest T" },
    ],
  },
  {
    key: "back",
    label: "Back",
    subsites: [{ key: "back", label: "Back" }],
  },
  {
    key: "underarms",
    label: "Underarms",
    subsites: [
      { key: "left_underarm", label: "Left underarm" },
      { key: "right_underarm", label: "Right underarm" },
    ],
  },
  {
    key: "torso",
    label: "Torso",
    subsites: [{ key: "torso", label: "Torso" }],
  },
];

const siteField = (siteKey) => `hair_removed_sites_${siteKey}`;
const siteDateField = (siteKey) =>
  `hair_removed_sites_${siteKey}_last_shaved_date`;
const siteUnsureField = (siteKey) =>
  `hair_removed_sites_${siteKey}_last_shaved_unsure`;
const siteLastCollectionField = (siteKey) =>
  `hair_removed_sites_${siteKey}_last_shaved_last_collection`;

export default function HairAndInfluencingSection({
  register,
  watch,
  setValue,
  trigger,
  errors,
  showErrors,
}) {
  // parent scalp cut question
  const cutInLast12Months = watch("hair_cut_in_last_12_months"); 

  // scalp hair cut follow-ups
  const cutUnsure = watch("hair_last_cut_unsure");

  // body hair removed in last 12 months (Yes/No)
  const removedBodyHair = watch("hair_removed_body_hair_last_12_months"); 

  // pregnancy
  const sexAtBirth = watch("sex_at_birth"); 
  const pregnancyLocked = sexAtBirth === "Male";
  const pregnant = watch("pregnant_last_12_months"); 
  const dueUnsure = watch("pregnancy_due_date_unsure");

  //  pregnancy not applicable + outcome
  const dueNotApplicable = watch("pregnancy_due_date_not_applicable"); 
  const pregnancyOutcome = watch("pregnancy_outcome"); 

  // dyed/bleached
  const dyedBleached = watch("hair_dyed_bleached");
  const dyedBleachedDateOption = watch("hair_last_dyed_bleached_date_option");

  // body hair site/group watches
  const bodyHairWatchFields = BODY_HAIR_SITE_GROUPS.flatMap((group) => [
    siteField(group.key),
    ...group.subsites.flatMap((site) => [
      siteField(site.key),
      siteDateField(site.key),
      siteUnsureField(site.key),
      siteLastCollectionField(site.key),
    ]),
  ]);

  const bodyHairWatchValues = bodyHairWatchFields.map((field) => watch(field));

  // influencing parent questions
  const thermal = watch("hair_thermal_applications");   
  const swimming = watch("frequent_swimming"); 
  const sunbeds = watch("frequent_sunbeds"); 
  const sprays = watch("frequent_sprays_on_sites"); 

  // spray sites
  const spraySiteValues = SPRAY_SITE_OPTIONS.map((site) =>
    watch(spraySiteField(site.key))
  );

  /** Scalp cut logic */

  // If parent is not Yes, clear all cut follow-ups
  useEffect(() => {
    if (cutInLast12Months !== "Yes") {
      setValue("hair_last_cut_date", "", { shouldDirty: false });
      setValue("hair_last_cut_unsure", false, { shouldDirty: false });
      setValue("hair_cut_shaved_to_skin", "", { shouldDirty: false });
    }
  }, [cutInLast12Months, setValue]);


  // Re-trigger validations when scalp-cut state changes
  useEffect(() => {
    trigger?.([
      "hair_cut_in_last_12_months",
      "hair_last_cut_date",
      "hair_last_cut_unsure",
      "hair_cut_shaved_to_skin",
    ]);
  }, [cutInLast12Months, cutUnsure, trigger]);

  /**Pregnancy logic*/
  useEffect(() => {
    if (!pregnancyLocked) return;

    // force parent answer to No
    setValue("pregnant_last_12_months", "No", { shouldDirty: true });

    // clear all pregnancy follow-ups
    setValue("pregnancy_due_or_birth_date", "", { shouldDirty: false });
    setValue("pregnancy_due_date_unsure", false, { shouldDirty: false });
    setValue("pregnancy_due_date_not_applicable", false, { shouldDirty: false });
    setValue("pregnancy_outcome", "", { shouldDirty: false });
    setValue("pregnancy_weeks", "", { shouldDirty: false });

    // revalidate in case pregnancy errors were showing
    trigger?.([
      "pregnant_last_12_months",
      "pregnancy_due_or_birth_date",
      "pregnancy_due_date_unsure",
      "pregnancy_due_date_not_applicable",
      "pregnancy_outcome",
      "pregnancy_weeks",
    ]);
  }, [pregnancyLocked, setValue, trigger]);

  // clear pregnancy date if unsure ticked
  useEffect(() => {
    if (dueUnsure) {
      setValue("pregnancy_due_or_birth_date", "", { shouldDirty: false });
      // mutual exclusion
      setValue("pregnancy_due_date_not_applicable", false, { shouldDirty: false });
      setValue("pregnancy_outcome", "", { shouldDirty: false });
    }
  }, [dueUnsure, setValue]);

  // clear pregnancy date if not-applicable ticked (and mutually exclude unsure)
  useEffect(() => {
    if (dueNotApplicable) {
      setValue("pregnancy_due_or_birth_date", "", { shouldDirty: false });
      setValue("pregnancy_due_date_unsure", false, { shouldDirty: false });
    } else {
      // if they untick not applicable, clear outcome
      setValue("pregnancy_outcome", "", { shouldDirty: false });
    }
  }, [dueNotApplicable, setValue]);

  // if not pregnant, clear pregnancy follow-ups
  useEffect(() => {
    if (pregnant !== "Yes") {
      setValue("pregnancy_due_or_birth_date", "", { shouldDirty: false });
      setValue("pregnancy_due_date_unsure", false, { shouldDirty: false });
      setValue("pregnancy_due_date_not_applicable", false, { shouldDirty: false });
      setValue("pregnancy_outcome", "", { shouldDirty: false });
      setValue("pregnancy_weeks", "", { shouldDirty: false });
    }
  }, [pregnant, setValue]);

  // Re-trigger pregnancy validations when relevant state changes
  useEffect(() => {
    trigger?.([
      "pregnancy_due_or_birth_date",
      "pregnancy_due_date_unsure",
      "pregnancy_due_date_not_applicable",
      "pregnancy_outcome",
    ]);
  }, [pregnant, dueUnsure, dueNotApplicable, pregnancyOutcome, trigger]);

  /** Dyed/bleached logic */

  // If not dyed/bleached, clear all hair dye follow-ups
  useEffect(() => {
    if (dyedBleached !== "Yes") {
      setValue("hair_last_dyed_bleached_date_option", "", { shouldDirty: false });
      setValue("hair_last_dyed_bleached_date", "", { shouldDirty: false });
      setValue("hair_dyed_bleached_additional_info", "", { shouldDirty: false });
    }
  }, [dyedBleached, setValue]);

  // If Unsure is selected, clear the date
  useEffect(() => {
    if (dyedBleachedDateOption === "Unsure") {
      setValue("hair_last_dyed_bleached_date", "", { shouldDirty: false });
    }
  }, [dyedBleachedDateOption, setValue]);

  // Re-trigger hair dye validation when the option changes
  useEffect(() => {
    trigger?.([
      "hair_last_dyed_bleached_date_option",
      "hair_last_dyed_bleached_date",
    ]);
  }, [dyedBleached, dyedBleachedDateOption, trigger]);

  /** Body hair removal logic */

  useEffect(() => {
    const clearSite = (siteKey) => {
      setValue(siteField(siteKey), false, { shouldDirty: false });
      setValue(siteDateField(siteKey), "", { shouldDirty: false });
      setValue(siteUnsureField(siteKey), false, { shouldDirty: false });
      setValue(siteLastCollectionField(siteKey), false, { shouldDirty: false });
    };

    if (removedBodyHair !== "Yes") {
      BODY_HAIR_SITE_GROUPS.forEach((group) => {
        setValue(siteField(group.key), false, { shouldDirty: false });
        group.subsites.forEach((site) => clearSite(site.key));
      });
      return;
    }

    BODY_HAIR_SITE_GROUPS.forEach((group) => {
      const groupSelected = watch(siteField(group.key));

      if (!groupSelected) {
        group.subsites.forEach((site) => clearSite(site.key));
        return;
      }

      group.subsites.forEach((site) => {
        const selected = watch(siteField(site.key));
        const unsure = watch(siteUnsureField(site.key));
        const lastCollection = watch(siteLastCollectionField(site.key));

        if (!selected) {
          setValue(siteDateField(site.key), "", { shouldDirty: false });
          setValue(siteUnsureField(site.key), false, { shouldDirty: false });
          setValue(siteLastCollectionField(site.key), false, {
            shouldDirty: false,
          });
          return;
        }

        if (unsure) {
          setValue(siteLastCollectionField(site.key), false, {
            shouldDirty: false,
          });
          return;
        }

        if (lastCollection) {
          setValue(siteDateField(site.key), "", { shouldDirty: false });
          setValue(siteUnsureField(site.key), false, { shouldDirty: false });
        }
      });
    });
  }, [removedBodyHair, setValue, watch, ...bodyHairWatchValues]);

  /** Influencing factor clearing */

  // clear thermal frequency if "No"
  useEffect(() => {
    if (thermal !== "Yes")
      setValue("hair_thermal_frequency", "", { shouldDirty: false });
  }, [thermal, setValue]);

  // clear swimming frequency if "No"
  useEffect(() => {
    if (swimming !== "Yes")
      setValue("frequent_swimming_frequency", "", { shouldDirty: false });
  }, [swimming, setValue]);

  // clear sunbeds frequency if "No"
  useEffect(() => {
    if (sunbeds !== "Yes")
      setValue("frequent_sunbeds_frequency", "", { shouldDirty: false });
  }, [sunbeds, setValue]);

  // sprays follow-ups clearing if "No"
  useEffect(() => {
    if (sprays !== "Yes") {
      setValue("frequent_sprays_frequency", "", { shouldDirty: false });

      SPRAY_SITE_OPTIONS.forEach((site) => {
        setValue(spraySiteField(site.key), false, { shouldDirty: false });
      });
    }
  }, [sprays, setValue]);

  // Re-trigger spray validation when parent/sites change
  useEffect(() => {
    trigger?.([
      "frequent_sprays_frequency",
      ...SPRAY_SITE_OPTIONS.map((site) => spraySiteField(site.key)),
    ]);
  }, [sprays, trigger, ...spraySiteValues]);

  // pregnancy weeks is NOT required

  const SiteFlags = ({ unsureName, lastCollectionName }) => {
    const unsureReg = register(unsureName);
    const lastReg = register(lastCollectionName);

    return (
      <div style={styles.unsureWrap}>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            {...unsureReg}
            onChange={(e) => {
              unsureReg.onChange(e);
              if (e.target.checked) {
                setValue(lastCollectionName, false, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            }}
          />
          Unsure
        </label>

        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            {...lastReg}
            onChange={(e) => {
              lastReg.onChange(e);
              if (e.target.checked) {
                setValue(unsureName, false, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            }}
          />
          Last FTS Collection
        </label>
      </div>
    );
  };

  const BodyHairSubSiteRow = ({ site, autoSelected = false }) => {
    const selected = autoSelected || watch(siteField(site.key));
    const unsure = watch(siteUnsureField(site.key));
    const lastCollection = watch(siteLastCollectionField(site.key));

    return (
      <>
        {!autoSelected && (
          <label style={styles.checkboxLabel}>
            <input type="checkbox" {...register(siteField(site.key))} />
            {site.label}
          </label>
        )}

        {selected && (
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>
                When did you last shave/remove hair from {site.label.toLowerCase()}?
              </label>
              <input
                style={styles.input}
                type="date"
                max={todayISO}
                disabled={lastCollection}
                {...register(siteDateField(site.key), {
                  validate: (v) => {
                    if (!selected) return true;
                    if (unsure || lastCollection) return true;

                    return (v || "").trim()
                      ? true
                      : "Please enter a date, tick Unsure, or tick Last FTS Collection";
                  },
                })}
              />
               {showErrors && errors?.[siteDateField(site.key)] && (
                <div style={styles.errorText}>
                  {errors[siteDateField(site.key)].message}
                </div>
              )}
            </div>

            <SiteFlags
              unsureName={siteUnsureField(site.key)}
              lastCollectionName={siteLastCollectionField(site.key)}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <section style={styles.section}>
      <h2 style={styles.h2}>
        Hair Cutting (within the 12 months prior to sampling)
      </h2>

      {/* Parent scalp cut question */}
      <div style={styles.field}>
        <label style={styles.label}>
          Have you cut your scalp hair in the 12 months prior to sample
          collection/since your previous test?
        </label>

        <div style={styles.inline}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              value="Yes"
              {...register("hair_cut_in_last_12_months", {
                required: "Please select an option",
              })}
            />{" "}
            Yes
          </label>

          <label style={styles.radioLabel}>
            <input
              type="radio"
              value="No"
              {...register("hair_cut_in_last_12_months", {
                required: "Please select an option",
              })}
            />{" "}
            No
          </label>
        </div>

        {showErrors && errors?.hair_cut_in_last_12_months && (
          <div style={styles.errorText}>
            {errors.hair_cut_in_last_12_months.message}
          </div>
        )}
      </div>

      {/* Only show cut follow-ups if Yes */}
      {cutInLast12Months === "Yes" && (
        <>
          {/* Last scalp hair cut date */}
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>
                When did you last cut your scalp hair?
              </label>
              <input
                style={styles.input}
                type="date"
                max={todayISO}
                {...register("hair_last_cut_date", {
                  validate: (v) => {
                    if (cutInLast12Months !== "Yes") return true;
                    if (cutUnsure) return true;

                    return (v || "").trim()
                      ? true
                      : "Please enter a date or tick Unsure";
                  },
                })}
              />
            </div>
            <div style={styles.unsureWrap}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" {...register("hair_last_cut_unsure")} />
                Unsure
              </label>
            </div>
          </div>

          {showErrors && errors?.hair_last_cut_date && (
            <div style={styles.errorText}>
              {errors.hair_last_cut_date.message}
            </div>
          )}

          {/* Was shaved to the skin? */}
          <div style={styles.field}>
            <label style={styles.label}>Was scalp hair shaved to the skin?</label>

            <div style={styles.inline}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  value="Yes"
                  {...register("hair_cut_shaved_to_skin", {
                    validate: (v) => {
                      if (cutInLast12Months !== "Yes") return true;
                      return v ? true : "Please select an option";
                    },
                  })}
                />{" "}
                Yes
              </label>

              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  value="No"
                  {...register("hair_cut_shaved_to_skin", {
                    validate: (v) => {
                      if (cutInLast12Months !== "Yes") return true;
                      return v ? true : "Please select an option";
                    },
                  })}
                />{" "}
                No
              </label>
            </div>

            {showErrors && errors?.hair_cut_shaved_to_skin && (
              <div style={styles.errorText}>
                {errors.hair_cut_shaved_to_skin.message}
              </div>
            )}
          </div>
        </>
      )}

      {/* Body hair removed yes/no */}
      <div style={styles.field}>
        <label style={styles.label}>
          Have you shaved (to the skin)/removed any body hair in the last 12 months?
        </label>

        <div style={styles.inline}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              value="Yes"
              {...register("hair_removed_body_hair_last_12_months", {
                required: "Please select an option",
              })}
            />{" "}
            Yes
          </label>

          <label style={styles.radioLabel}>
            <input
              type="radio"
              value="No"
              {...register("hair_removed_body_hair_last_12_months", {
                required: "Please select an option",
              })}
            />{" "}
            No
          </label>
        </div>

        {showErrors && errors?.hair_removed_body_hair_last_12_months && (
          <div style={styles.errorText}>
            {errors.hair_removed_body_hair_last_12_months.message}
          </div>
        )}
      </div>

      {/* Only show sites if Yes */}
      {removedBodyHair === "Yes" && (
        <>
          <div style={styles.field}>
            <label style={styles.label}>Where did you remove hair from?</label>

            <div style={styles.checkboxGrid}>
              {BODY_HAIR_SITE_GROUPS.map((group) => (
                <label key={group.key} style={styles.checkboxLabel}>
                  <input type="checkbox" {...register(siteField(group.key))} />
                  {group.label}
                </label>
              ))}
            </div>
          </div>

          {BODY_HAIR_SITE_GROUPS.map((group) => {
            const groupSelected = watch(siteField(group.key));

            if (!groupSelected) return null;

            return (
              <div key={group.key} style={styles.subSiteBox}>
                <label style={styles.label}>{group.label} details</label>

                <div style={styles.subSiteGrid}>
                  {group.subsites.map((site) => (
                    <div key={site.key} style={styles.subSiteItem}>
                      <BodyHairSubSiteRow
                        site={site}
                        autoSelected={group.subsites.length === 1}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
      <hr style={styles.hr} />

      <h2 style={styles.h2}>Influencing Factors</h2>

      {/* Pregnant */}
      <div style={styles.field}>
        <label style={styles.label}>
          Are you currently pregnant or have you been pregnant in the 12 months
          prior sampling?
        </label>

        <div style={styles.inline}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              value="Yes"
              disabled={pregnancyLocked}
              {...register("pregnant_last_12_months", {
                validate: (v) => {
                  if (pregnancyLocked) return true;         // Male: not applicable
                  return v ? true : "Please select an option";
               },
              })}
            />{" "}
            Yes
          </label>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              value="No"
              disabled={pregnancyLocked}
              {...register("pregnant_last_12_months", {
                validate: (v) => {
                  if (pregnancyLocked) return true;         // Male: not applicable
                  return v ? true : "Please select an option";
                },
              })}
            />{" "}
            No
          </label>
        </div>

        {showErrors && errors?.pregnant_last_12_months && (
          <div style={styles.errorText}>
            {errors.pregnant_last_12_months.message}
          </div>
        )}
      </div>

      {/* Due date / birth date */}
      <div style={styles.row}>
        <div style={styles.field}>
          <label style={styles.label}>
            If so, when is your due date/when did you give birth?
          </label>
          <input
            style={styles.input}
            type="date"
            disabled={pregnancyLocked || pregnant !== "Yes" || dueUnsure || dueNotApplicable}
            {...register("pregnancy_due_or_birth_date", {
              validate: (v) => {
                if (pregnancyLocked) return true;
                if (pregnant !== "Yes") return true;
                if (dueUnsure || dueNotApplicable) return true;
                return (v || "").trim()
                  ? true
                  : "Please enter a due/birth date or tick Unsure / Not applicable";
              },
            })}
          />
        </div>

        <div style={styles.unsureWrap}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              disabled={pregnant !== "Yes"}
              {...register("pregnancy_due_date_unsure", {
                validate: (v) => {
                  if (pregnancyLocked) return true;
                  if (pregnant !== "Yes") return true;
                  if (v) return true;
                  const naVal = watch("pregnancy_due_date_not_applicable");
                  if (naVal) return true;
                  const dateVal = watch("pregnancy_due_or_birth_date");
                  return (dateVal || "").trim()
                    ? true
                    : "Please enter a due/birth date or tick Unsure / Not applicable";
                },
              })}
              onChange={(e) => {
                const checked = e.target.checked;
                setValue("pregnancy_due_date_unsure", checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                if (checked) {
                  setValue("pregnancy_due_date_not_applicable", false, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue("pregnancy_outcome", "", {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
            />
            Unsure
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              disabled={pregnancyLocked || pregnant !== "Yes"}
              {...register("pregnancy_due_date_not_applicable", {
                validate: (v) => {
                  if (pregnancyLocked) return true;
                  if (pregnant !== "Yes") return true;
                  if (v) return true;
                  const unsureVal = watch("pregnancy_due_date_unsure");
                  if (unsureVal) return true;
                  const dateVal = watch("pregnancy_due_or_birth_date");
                  return (dateVal || "").trim()
                    ? true
                    : "Please enter a due/birth date or tick Unsure / Not applicable";
                },
              })}
              onChange={(e) => {
                const checked = e.target.checked;
                setValue("pregnancy_due_date_not_applicable", checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                if (checked) {
                  setValue("pregnancy_due_date_unsure", false, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                } else {
                  setValue("pregnancy_outcome", "", {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
            />
            Not applicable
          </label>
        </div>
      </div>

      {showErrors &&
        (errors?.pregnancy_due_or_birth_date ||
          errors?.pregnancy_due_date_unsure ||
          errors?.pregnancy_due_date_not_applicable) && (
          <div style={styles.errorText}>
            {errors?.pregnancy_due_or_birth_date?.message ||
              errors?.pregnancy_due_date_unsure?.message ||
              errors?.pregnancy_due_date_not_applicable?.message}
          </div>
        )}

      {/* Outcome only if Not applicable */}
      {pregnant === "Yes" && dueNotApplicable && (
        <div style={styles.field}>
          <label style={styles.label}>Reason (if not applicable)</label>
          <select
            style={styles.select}
            {...register("pregnancy_outcome", {
              validate: (v) => {
                if (pregnancyLocked) return true;         
                if (pregnant !== "Yes") return true;
                if (!dueNotApplicable) return true;
                return v ? true : "Please select an option";
              },
            })}
          >
            <option value="">Choose an item</option>
            <option value="Termination">Termination</option>
            <option value="Miscarriage">Miscarriage</option>
            <option value="Miscarriage in 3rd trimester">
              Miscarriage in 3rd trimester
            </option>
          </select>

          {showErrors && errors?.pregnancy_outcome && (
            <div style={styles.errorText}>{errors.pregnancy_outcome.message}</div>
          )}
        </div>
      )}

      {/* Weeks pregnant (NOT required) */}
      <div style={styles.field}>
        <label style={styles.label}>
          How many weeks pregnant were you when you gave birth? (if applicable)
        </label>

        <select
          style={styles.select}
          disabled={pregnancyLocked || pregnant !== "Yes"}
          {...register("pregnancy_weeks")}
        >
          <option value="">Choose an item</option>
          <option value="Unsure">Unsure</option>
          {Array.from({ length: 45 }, (_, i) => i)
            .reverse()
            .map((w) => (
              <option key={w} value={String(w)}>
                {w}
              </option>
            ))}
        </select>

        {showErrors && errors?.pregnancy_weeks && (
          <div style={styles.errorText}>{errors.pregnancy_weeks.message}</div>
        )}
      </div>

      {/* Dyed/bleached */}
      <YesNo
        label="Have you dyed/bleached your hair in the 12 months prior to sampling?"
        name="hair_dyed_bleached"
        register={register}
        required={true}
        requiredMessage="Please select an option"
        errors={errors}
        showErrors={showErrors}
      />

      {dyedBleached === "Yes" && (
        <>
          <div style={styles.field}>
            <label style={styles.label}>
              To the best of your knowledge, when did you last dye/bleach your
              scalp hair?
            </label>

            <div style={styles.inline}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  value="Unsure"
                  {...register("hair_last_dyed_bleached_date_option", {
                    validate: (v) => {
                      if (dyedBleached !== "Yes") return true;
                      return v
                        ? true
                        : "Please select Unsure, Exact date, or Approx date";
                    },
                  })}
                />{" "}
                Unsure
              </label>

              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  value="Exact date"
                  {...register("hair_last_dyed_bleached_date_option", {
                    validate: (v) => {
                      if (dyedBleached !== "Yes") return true;
                      return v
                        ? true
                        : "Please select Unsure, Exact date, or Approx date";
                    },
                  })}
                />{" "}
                Exact date
              </label>

              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  value="Approx date"
                  {...register("hair_last_dyed_bleached_date_option", {
                    validate: (v) => {
                      if (dyedBleached !== "Yes") return true;
                      return v
                        ? true
                        : "Please select Unsure, Exact date, or Approx date";
                    },
                  })}
                />{" "}
                Approx Date
              </label>
            </div>

            {showErrors && errors?.hair_last_dyed_bleached_date_option && (
              <div style={styles.errorText}>
                {errors.hair_last_dyed_bleached_date_option.message}
              </div>
            )}
          </div>

          {(dyedBleachedDateOption === "Exact date" ||
            dyedBleachedDateOption === "Approx date") && (
            <div style={styles.field}>
              <label style={styles.label}>
                {dyedBleachedDateOption === "Approx date"
                  ? "Approximate date"
                  : "Exact date"}
              </label>

              <input
                style={styles.input}
                type="date"
                max={todayISO}
                {...register("hair_last_dyed_bleached_date", {
                  validate: (v) => {
                    if (dyedBleached !== "Yes") return true;

                    if (
                      dyedBleachedDateOption !== "Exact date" &&
                      dyedBleachedDateOption !== "Approx date"
                    ) {
                      return true;
                    }

                    return (v || "").trim() ? true : "Please enter a date";
                  },
                })}
              />

              {showErrors && errors?.hair_last_dyed_bleached_date && (
                <div style={styles.errorText}>
                  {errors.hair_last_dyed_bleached_date.message}
                </div>
              )}
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>
              Additional information about hair dye/bleach
            </label>

            <textarea
              style={styles.textarea}
              rows={3}
              placeholder="Add any additional information here"
              {...register("hair_dyed_bleached_additional_info")}
            />
          </div>
        </>
      )}

      {/* Thermal applications */}
      <YesNo
        label="Have you used thermal applications (e.g. hair straighteners) applied from the roots of the scalp hair?"
        name="hair_thermal_applications"
        register={register}
        required={true}
        requiredMessage="Please select an option"
        errors={errors}
        showErrors={showErrors}
      />

      {thermal === "Yes" && (
        <div style={styles.field}>
          <label style={styles.label}>How often do you use thermal applications?</label>
          <select style={styles.select} {...register("hair_thermal_frequency")}>
            <option value="">Choose an item</option>
            {FREQ_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Wash frequency */}
      <div style={styles.field}>
        <label style={styles.label}>How often do you wash your hair?</label>
        <select style={styles.select} {...register("hair_wash_frequency")}>
          <option value="">Choose an item</option>
          <option value="Daily">Daily</option>
          <option value="Every other day">Every other day</option>
          <option value="2–3 times per week">2–3 times per week</option>
          <option value="Weekly">Weekly</option>
          <option value="Less than weekly">Less than weekly</option>
          <option value="Unsure">Unsure</option>
        </select>
      </div>

      {/* Nails contact with bleach */}
      <div style={styles.field}>
        <label style={styles.label}>Have your nails had direct contact with bleach?</label>
        <select style={styles.select} {...register("nails_contact_bleach")}>
          <option value="">Choose an item</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
          <option value="Unsure">Unsure</option>
        </select>
      </div>

      {/* Swim/hot tubs */}
      <YesNo
        label="Do you swim in a pool or use hot tubs and submerge your head?"
        name="frequent_swimming"
        register={register}
        required={true}
        requiredMessage="Please select an option"
        errors={errors}
        showErrors={showErrors}
      />

      {swimming === "Yes" && (
        <div style={styles.field}>
          <label style={styles.label}>How often do you swim or use hot tubs?</label>
          <select style={styles.select} {...register("frequent_swimming_frequency")}>
            <option value="">Choose an item</option>
            {FREQ_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Sunbeds */}
      <YesNo
        label="Do you use sunbeds?"
        name="frequent_sunbeds"
        register={register}
        required={true}
        requiredMessage="Please select an option"
        errors={errors}
        showErrors={showErrors}
      />

      {sunbeds === "Yes" && (
        <div style={styles.field}>
          <label style={styles.label}>How often do you use sunbeds?</label>
          <select style={styles.select} {...register("frequent_sunbeds_frequency")}>
            <option value="">Choose an item</option>
            {FREQ_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Sprays / aerosol products */}
      <YesNo
        label="Have you used any aerosol products, such as hairspray, perfume/aftershave, deodorant and/or dry shampoo during the 12 months prior to sampling?"
        name="frequent_sprays_on_sites"
        register={register}
        required={true}
        requiredMessage="Please select an option"
        errors={errors}
        showErrors={showErrors}
      />

      {sprays === "Yes" && (
        <>
          <div style={styles.field}>
            <label style={styles.label}>
              How often have you used these aerosol products?
            </label>

            <select
              style={styles.select}
              {...register("frequent_sprays_frequency", {
                validate: (v) => {
                  if (sprays !== "Yes") return true;
                  return v ? true : "Please select a frequency";
                },
              })}
            >
              <option value="">Choose an item</option>
              {FREQ_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>

            {showErrors && errors?.frequent_sprays_frequency && (
              <div style={styles.errorText}>
                {errors.frequent_sprays_frequency.message}
              </div>
            )}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>
              What sites has this been applied to? Choose all that are appropriate.
            </label>

            <div style={styles.checkboxGrid}>
              {SPRAY_SITE_OPTIONS.map((site) => (
                <label key={site.key} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    {...register(spraySiteField(site.key), {
                      validate: () => {
                        if (sprays !== "Yes") return true;

                        const anySelected = SPRAY_SITE_OPTIONS.some((option) =>
                          watch(spraySiteField(option.key))
                        );

                        return anySelected
                          ? true
                          : "Please select at least one site";
                      },
                    })}
                  />
                  {site.label}
                </label>
              ))}
            </div>

            {showErrors &&
              SPRAY_SITE_OPTIONS.some(
                (site) => errors?.[spraySiteField(site.key)]
              ) && (
                <div style={styles.errorText}>
                  {
                    SPRAY_SITE_OPTIONS.map(
                      (site) => errors?.[spraySiteField(site.key)]
                    ).find(Boolean)?.message
                  }
                </div>
              )}
          </div>
        </>
      )}
    </section>
  );
}

function YesNo({
  label,
  name,
  register,
  required = false,
  requiredMessage = "Please select an option",
  errors,
  showErrors,
}) {
  const rules = required ? { required: requiredMessage } : undefined;
  const err = errors?.[name];

  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>

      <div style={styles.inline}>
        <label style={styles.radioLabel}>
          <input type="radio" value="Yes" {...register(name, rules)} /> Yes
        </label>
        <label style={styles.radioLabel}>
          <input type="radio" value="No" {...register(name, rules)} /> No
        </label>
      </div>

      {showErrors && err && (
        <div style={styles.errorText}>{err.message || requiredMessage}</div>
      )}
    </div>
  );
}

const styles = {
  section: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  h2: { marginBottom: 12, color: "#904369" },
  hr: { margin: "18px 0", border: "none", borderTop: "1px solid #eee" },

  row: { display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" },
  field: { display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 },
  label: { fontWeight: 600 },

  input: {
    padding: 8,
    borderRadius: 6,
    border: "1px solid #ccc",
    minWidth: 240,
  },
  select: { padding: 8, borderRadius: 6, border: "1px solid #ccc", minWidth: 280 },

  textarea: {
    padding: 8,
    borderRadius: 6,
    border: "1px solid #ccc",
    minWidth: 280,
    fontFamily: "inherit",
    resize: "vertical",
  },

  unsureWrap: {
    paddingBottom: 2,
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },

  checkboxLabel: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    fontWeight: 600,
  },

  subSiteBox: {
    border: "1px solid #eee",
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
    background: "#fafafa",
  },

  subSiteGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 8,
  },

  subSiteItem: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  inline: { display: "flex", gap: 18, alignItems: "center" },
  radioLabel: { display: "flex", gap: 8, alignItems: "center" },

  checkboxGrid: { display: "flex", gap: 18, flexWrap: "wrap" },

  errorText: { color: "crimson", fontSize: 12, marginTop: 4 },
};
